import { useEffect, useState } from 'react';
import { useLocationAutocompleteQuery } from './api.js';

/** Debounces locally (250ms) rather than on every keystroke — `/prospecting
 * /autocomplete` proxies a real, billed Google Places call, same reasoning
 * `useProspectSearchQuery` only fires on an explicit Search click, just a
 * lighter touch here since typing into this field is the whole point. */
export function LocationAutocomplete({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const [open, setOpen] = useState(false);
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), 250);
    return () => clearTimeout(t);
  }, [value]);

  const { data: suggestions } = useLocationAutocompleteQuery(debounced);
  const list = suggestions ?? [];

  return (
    <div className="relative">
      <input
        type="text"
        value={value}
        onChange={(e) => {
          onChange(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        placeholder="City, state or address"
        className="h-9 w-full rounded-control border border-border-strong bg-surface px-3 text-sm font-normal"
        autoComplete="off"
      />
      {open && list.length > 0 && (
        <ul
          className="absolute z-10 mt-1 max-h-64 w-full min-w-[260px] overflow-y-auto rounded-control border border-border bg-surface shadow-dialog"
          style={{ listStyle: 'none' }}
        >
          {list.map((s) => (
            <li key={s.placeId}>
              <button
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => {
                  onChange(s.secondaryText ? `${s.mainText}, ${s.secondaryText}` : s.mainText);
                  setOpen(false);
                }}
                className="flex w-full flex-col items-start px-3 py-2 text-left text-sm hover:bg-surface-subtle"
              >
                <span className="font-medium text-ink">{s.mainText}</span>
                {s.secondaryText && <span className="text-xs text-text-muted">{s.secondaryText}</span>}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
